import { Link, useLocation } from "wouter";
import {
  Calendar,
  DollarSign,
  MessageSquare,
  User,
  Home,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "@/hooks/use-mobile";
import { useIsCleaner } from "@/hooks/use-role";
import { useQuery } from "@tanstack/react-query";

interface NavItem {
  icon: React.ElementType;
  label: string;
  href: string;
}

export function CleanerMobileNav() {
  const { t } = useTranslation(["profile", "common"]);
  const isMobile = useIsMobile();
  const isCleaner = useIsCleaner();
  const [location] = useLocation();

  // Fetch unread message count
  const { data: unreadData } = useQuery<{ count: number }>({
    queryKey: ["/api/messages/unread"],
    enabled: isCleaner && isMobile,
  });
  const unreadCount = unreadData?.count ?? 0;

  const navItems: NavItem[] = [
    {
      icon: Home,
      label: t("common:home", "Accueil"),
      href: "/profile",
    },
    {
      icon: Calendar,
      label: t("profile:quickActions.bookings", "Réservations"),
      href: "/bookings",
    },
    {
      icon: MessageSquare,
      label: t("profile:quickActions.messages", "Messages"),
      href: "/messages",
    },
    {
      icon: DollarSign,
      label: t("profile:quickActions.payouts", "Paiements"),
      href: "/profile/payouts",
    },
  ];

  // Only show for cleaners on mobile
  if (!isMobile || !isCleaner) {
    return null;
  }

  // Don't show on certain pages where it might conflict
  const hideOnPaths = ["/login", "/register", "/onboarding", "/book"];
  if (hideOnPaths.some((path) => location.startsWith(path))) {
    return null;
  }

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 bg-background border-t safe-area-bottom">
      <div className="flex items-center justify-around h-16 px-2">
        {navItems.map((item) => {
          const isActive = location === item.href ||
            (item.href !== "/profile" && location.startsWith(item.href));
          const Icon = item.icon;

          return (
            <Link key={item.href} href={item.href}>
              <button
                className={`flex flex-col items-center justify-center min-w-[64px] h-full px-2 py-1 rounded-lg transition-colors ${
                  isActive
                    ? "text-primary bg-primary/10"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                }`}
              >
                <div className="relative">
                  <Icon className="h-5 w-5" />
                  {item.icon === MessageSquare && unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-medium">
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </div>
                <span className="text-[10px] sm:text-xs mt-0.5 font-medium truncate max-w-[56px]">
                  {item.label}
                </span>
              </button>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}

// Add bottom padding to page content when nav is visible
export function CleanerMobileNavSpacer() {
  const isMobile = useIsMobile();
  const isCleaner = useIsCleaner();
  const [location] = useLocation();

  const hideOnPaths = ["/login", "/register", "/onboarding", "/book"];
  const shouldShow = isMobile && isCleaner && !hideOnPaths.some((path) => location.startsWith(path));

  if (!shouldShow) {
    return null;
  }

  return <div className="h-16 safe-area-bottom" />;
}
